/**
 * F-186 / T-142-001 + T-142-002 — ROI Calculator page.
 *
 * Side-by-side per-use vs commitment pricing comparison. The per-use model is
 * a blended per-call cost across the a la carte compliance services; the
 * commitment model is the monthly plan (Essentials / Professional) with its
 * included call allowance. All math is a pure function so the page is
 * deterministic and unit-testable; numbers are USD.
 */
import React, { useMemo, useState } from 'react';

export const PER_USE_AVG_USD = 33;
export const DEFAULT_CALLS_PER_PROPERTY_PER_YEAR = 4;

export interface RoiPlan {
    tier: 'essentials' | 'professional';
    label: string;
    monthlyUsd: number;
    includedCallsPerMonth: number;
    tagline: string;
    features: string[];
}

export const ROI_PLANS: RoiPlan[] = [
    {
        tier: 'essentials',
        label: 'Essentials',
        monthlyUsd: 99,
        includedCallsPerMonth: 5,
        tagline: 'For operators just getting compliant',
        features: [
            '5 compliance checks per month included',
            'Registration + inspection reminders',
            'Pre-filled Texas city forms',
            'Single-license support',
        ],
    },
    {
        tier: 'professional',
        label: 'Professional',
        monthlyUsd: 199,
        includedCallsPerMonth: 20,
        tagline: 'For multi-city operators at volume',
        features: [
            '20 compliance checks per month included',
            'Dallas + Houston cross-city dashboard',
            'HRRB + high-risk citation tracking',
            'Priority support + full template library',
        ],
    },
];

export interface RoiResult {
    tier: RoiPlan['tier'];
    label: string;
    annualCallsNeeded: number;
    perUseAnnualUsd: number;
    commitmentAnnualUsd: number;
    savingsUsd: number;
    includedAnnualCalls: number;
    overflowCalls: number;
}

export function annualCallsNeeded(input: { propertyCount: number; callsPerPropertyPerYear: number }): number {
    return input.propertyCount * input.callsPerPropertyPerYear;
}

export function perUseAnnualCost(input: { propertyCount: number; callsPerPropertyPerYear: number }): number {
    return annualCallsNeeded(input) * PER_USE_AVG_USD;
}

export function commitmentAnnualCost(plan: RoiPlan): number {
    return plan.monthlyUsd * 12;
}

export function comparePlan(input: { propertyCount: number; callsPerPropertyPerYear: number }, plan: RoiPlan): RoiResult {
    const calls = annualCallsNeeded(input);
    const perUseAnnualUsd = calls * PER_USE_AVG_USD;
    const commitmentAnnualUsd = commitmentAnnualCost(plan);
    const includedAnnualCalls = plan.includedCallsPerMonth * 12;
    return {
        tier: plan.tier,
        label: plan.label,
        annualCallsNeeded: calls,
        perUseAnnualUsd,
        commitmentAnnualUsd,
        savingsUsd: Math.max(0, perUseAnnualUsd - commitmentAnnualUsd),
        includedAnnualCalls,
        overflowCalls: Math.max(0, calls - includedAnnualCalls),
    };
}

/**
 * Total annual cost of a plan including paying per-use for any calls beyond the
 * plan's included allowance. This is the honest apples-to-apples comparison —
 * raw savings always favours the cheaper plan regardless of whether it covers
 * the operator's volume.
 */
export function effectiveAnnualCost(input: { propertyCount: number; callsPerPropertyPerYear: number }, plan: RoiPlan): number {
    const calls = annualCallsNeeded(input);
    const includedAnnualCalls = plan.includedCallsPerMonth * 12;
    const overflow = Math.max(0, calls - includedAnnualCalls);
    return commitmentAnnualCost(plan) + overflow * PER_USE_AVG_USD;
}

/**
 * The plan with the lowest total annual cost (commitment + overflow), or null
 * when staying purely per-use is cheaper than every commitment.
 */
export function recommendedTier(input: { propertyCount: number; callsPerPropertyPerYear: number }): RoiPlan['tier'] | null {
    const perUse = perUseAnnualCost(input);
    const [ess, pro] = ROI_PLANS.map((p) => ({ tier: p.tier, cost: effectiveAnnualCost(input, p) }));
    if (perUse <= ess.cost && perUse <= pro.cost) return null;
    return ess.cost <= pro.cost ? 'essentials' : 'professional';
}

export const formatUsd = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;

export interface PropertyCountParse {
    value: number | null;
    error?: string;
}

const INVALID_REASONS: Array<[RegExp, string]> = [
    [/^\s*$/, 'Enter a property count.'],
    [/^\s*0/, 'Property count must be at least 1.'],
];

export function parsePropertyCount(raw: string): PropertyCountParse {
    const trimmed = String(raw ?? '').trim();
    for (const [re, msg] of INVALID_REASONS) {
        if (re.test(trimmed)) return { value: null, error: msg };
    }
    const n = Number(trimmed);
    if (!Number.isFinite(n) || !Number.isInteger(n)) {
        return { value: null, error: 'Property count must be a whole number.' };
    }
    if (n < 1) return { value: null, error: 'Property count must be at least 1.' };
    if (n > 9999) return { value: null, error: 'Enter a property count up to 9,999.' };
    return { value: n };
}

export const ROICalculatorPage: React.FC = () => {
    const [propertyRaw, setPropertyRaw] = useState('12');
    const [callsPerProperty, setCallsPerProperty] = useState(DEFAULT_CALLS_PER_PROPERTY_PER_YEAR);

    const parsed = useMemo(() => parsePropertyCount(propertyRaw), [propertyRaw]);
    const valid = parsed.value !== null;
    const calls = Math.max(1, Math.min(24, Number(callsPerProperty) || DEFAULT_CALLS_PER_PROPERTY_PER_YEAR));

    const input = valid
        ? { propertyCount: parsed.value as number, callsPerPropertyPerYear: calls }
        : { propertyCount: 1, callsPerPropertyPerYear: calls };

    const comparisons = useMemo(() => ROI_PLANS.map((p) => comparePlan(input, p)), [input]);
    const perUseTotal = perUseAnnualCost(input);
    const recommended = valid ? recommendedTier(input) : null;

    return (
        <div className="max-w-7xl mx-auto px-4 py-10" data-testid="roi-page">
            <header className="mb-8">
                <h1 className="text-3xl font-bold text-gray-800 mb-2">Compliance ROI Calculator</h1>
                <p className="text-gray-500 max-w-3xl">
                    Compare paying per check against an Essentials or Professional commitment. The side-by-side
                    is driven by your property count and how many compliance checks each property needs per year.
                </p>
            </header>

            <div className="bg-white rounded-xl shadow p-6 mb-8">
                <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                        <label htmlFor="roi-property-count" className="block text-sm font-medium text-gray-700 mb-1">
                            Properties in portfolio
                        </label>
                        <input
                            id="roi-property-count"
                            type="text"
                            inputMode="numeric"
                            value={propertyRaw}
                            data-testid="roi-property-count"
                            onChange={(e) => setPropertyRaw(e.target.value)}
                            className="border rounded-lg px-3 py-2 w-full focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                        {!valid && (
                            <p className="text-sm text-red-600 mt-1" data-testid="roi-property-error">
                                {parsed.error}
                            </p>
                        )}
                    </div>
                    <div>
                        <label htmlFor="roi-calls-input" className="block text-sm font-medium text-gray-700 mb-1">
                            Compliance checks per property / year
                        </label>
                        <input
                            id="roi-calls-input"
                            type="number"
                            min={1}
                            max={24}
                            value={callsPerProperty}
                            data-testid="roi-calls-input"
                            onChange={(e) => setCallsPerProperty(Number(e.target.value))}
                            className="border rounded-lg px-3 py-2 w-full focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                        <p className="text-xs text-gray-400 mt-1">
                            Registrations, inspections, deposit closes, and rekey events each count as one check.
                        </p>
                    </div>
                </div>

                {valid && (
                    <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4" data-testid="roi-per-use-basis">
                        <p className="text-sm text-gray-700">
                            At <span className="font-semibold">{annualCallsNeeded(input).toLocaleString('en-US')}</span> checks
                            per year, staying per-use costs about{' '}
                            <span className="font-bold" data-testid="roi-per-use-annual">{formatUsd(perUseTotal)}</span>{' '}
                            annually (avg <span className="font-semibold">{formatUsd(PER_USE_AVG_USD)}</span>/check).
                        </p>
                    </div>
                )}
            </div>

            <div className="grid gap-6 md:grid-cols-2">
                {ROI_PLANS.map((plan, i) => {
                    const cmp = comparisons[i];
                    const isRecommended = recommended === plan.tier;
                    return (
                        <section
                            key={plan.tier}
                            className={`relative bg-white rounded-xl shadow p-6 border-2 ${isRecommended ? 'border-green-500' : 'border-transparent'}`}
                            data-testid={`roi-plan-${plan.tier}`}
                        >
                            {isRecommended && (
                                <span
                                    className="absolute -top-3 left-6 bg-green-600 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full"
                                    data-testid="roi-recommended"
                                >
                                    Recommended
                                </span>
                            )}
                            <h2 className="text-lg font-bold text-gray-800">{plan.label}</h2>
                            <p className="text-sm text-gray-500 mb-4">{plan.tagline}</p>
                            <p className="text-2xl font-bold text-gray-900 mb-1">{formatUsd(plan.monthlyUsd)}<span className="text-base font-normal text-gray-500">/mo</span></p>
                            <p className="text-sm text-gray-500 mb-4" data-testid={`roi-commitment-annual-${plan.tier}`}>
                                {formatUsd(cmp.commitmentAnnualUsd)}/yr commitment
                            </p>

                            <dl className="border-t border-gray-100 pt-4 mb-4 space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <dt className="text-gray-500">Per-use equivalent</dt>
                                    <dd className="font-semibold text-gray-800">{formatUsd(cmp.perUseAnnualUsd)}/yr</dd>
                                </div>
                                <div className="flex justify-between">
                                    <dt className="text-gray-500">Included checks / yr</dt>
                                    <dd className="font-semibold text-gray-800">{cmp.includedAnnualCalls.toLocaleString('en-US')}</dd>
                                </div>
                                <div className="flex justify-between">
                                    <dt className="text-gray-500">Overflow checks (per-use)</dt>
                                    <dd className="font-semibold text-gray-800">{cmp.overflowCalls.toLocaleString('en-US')}</dd>
                                </div>
                            </dl>

                            <div
                                className={`rounded-lg p-3 mb-4 text-sm font-semibold ${cmp.savingsUsd > 0 ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'}`}
                                data-testid={`roi-savings-${plan.tier}`}
                            >
                                {cmp.savingsUsd > 0
                                    ? <>You drop <span className="font-bold">{formatUsd(cmp.savingsUsd)}</span> vs per-use.</>
                                    : <>Per-use is cheaper at this volume — grow into this plan.</>}
                            </div>

                            <ul className="space-y-2 mb-6 text-sm text-gray-700">
                                {plan.features.map((f) => (
                                    <li key={f} className="flex items-start gap-2">
                                        <i className="fas fa-check text-green-600 mt-0.5" aria-hidden="true" />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>

                            <a
                                href={`/signup?plan=${plan.tier}`}
                                className="block text-center bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg px-4 py-2.5 transition-colors"
                                data-testid="roi-trial-cta"
                            >
                                Start 14-day trial
                            </a>
                            <a href="/pricing" className="block text-center text-sm text-gray-500 hover:text-gray-700 mt-3">
                                See full pricing details
                            </a>
                        </section>
                    );
                })}
            </div>
        </div>
    );
};

export default ROICalculatorPage;